import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Search,
  CheckCircle2,
  Clock,
  FileCheck,
  DollarSign,
  ChevronRight,
  Car,
  Calendar,
  Shield,
  ArrowRight,
  Phone,
  Mail,
  HelpCircle,
  Plus,
  User,
  FileText,
  Lock,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { Progress } from "../components/ui/progress";
import { Label } from "../components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const milestones = [
  { id: "submitted", label: "Submitted", description: "Claim received and logged", icon: FileCheck, statuses: ["new"] },
  { id: "under_review", label: "Under Review", description: "Verification in progress", icon: Clock, statuses: ["under_review", "vin_verification", "valuation_calculated"] },
  { id: "decision", label: "Decision Made", description: "Claim evaluated", icon: Shield, statuses: ["approved", "escalated"] },
  { id: "compensation", label: "Compensation", description: "Payment processed", icon: DollarSign, statuses: ["closed"] },
];

function getMilestoneProgress(status) {
  if (["new"].includes(status)) return { current: 0, percentage: 25 };
  if (["under_review", "vin_verification"].includes(status)) return { current: 1, percentage: 50 };
  if (["valuation_calculated"].includes(status)) return { current: 1, percentage: 65 };
  if (["approved", "escalated"].includes(status)) return { current: 2, percentage: 85 };
  if (["closed"].includes(status)) return { current: 3, percentage: 100 };
  return { current: 0, percentage: 0 };
}

function ClaimStatusCard({ claim, onClick }) {
  const { current, percentage } = getMilestoneProgress(claim.status);
  
  const getStatusMessage = () => {
    switch (claim.status) {
      case "new": return "Your claim has been submitted and is being processed.";
      case "under_review": return "Our team is reviewing your claim documentation.";
      case "vin_verification": return "Verifying vehicle information and accident details.";
      case "valuation_calculated": return "Diminished value has been calculated. Decision pending.";
      case "approved": return "Great news! Your claim has been approved.";
      case "escalated": return "Your claim requires additional review by our team.";
      case "closed": return "Your claim is complete. Payment has been processed.";
      default: return "Processing your claim.";
    }
  };

  const formatCurrency = (value) => {
    if (!value && value !== 0) return "$0";
    const num = Number(value);
    const hasDecimals = num % 1 !== 0;
    return new Intl.NumberFormat("en-US", { 
      style: "currency", 
      currency: "USD", 
      minimumFractionDigits: hasDecimals ? 2 : 0, 
      maximumFractionDigits: hasDecimals ? 2 : 0 
    }).format(num);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  return (
    <div
      className="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-lg hover:border-slate-300 transition-all duration-300 cursor-pointer group"
      onClick={onClick}
      data-testid={`claim-status-card-${claim.claim_id}`}
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-[#2563EB] to-[#3B82F6] px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-blue-100 text-xs uppercase tracking-wide">Claim Number</p>
            <p className="text-white font-bold text-xl font-['Plus_Jakarta_Sans']">{claim.claim_id}</p>
          </div>
          <Badge className={`${claim.status === "approved" || claim.status === "closed" ? "bg-emerald-500" : claim.status === "escalated" ? "bg-amber-500" : "bg-white/20"} text-white border-0`}>
            {claim.status === "approved" ? "Approved" : claim.status === "closed" ? "Complete" : claim.status === "escalated" ? "Under Review" : "In Progress"}
          </Badge>
        </div>
      </div>

      {/* Progress Section */}
      <div className="px-6 py-5">
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-600">Claim Progress</span>
            <span className="text-sm font-semibold text-[#2563EB]">{percentage}%</span>
          </div>
          <Progress value={percentage} className="h-2" />
        </div>

        {/* Milestones */}
        <div className="flex items-center justify-between mb-6">
          {milestones.map((milestone, idx) => {
            const isComplete = idx < current;
            const isCurrent = idx === current;
            const Icon = milestone.icon;
            return (
              <div key={milestone.id} className="flex flex-col items-center relative">
                {idx > 0 && (
                  <div className={`absolute top-4 right-full w-full h-0.5 -mr-2 ${isComplete ? "bg-emerald-500" : "bg-slate-200"}`} style={{ width: "calc(100% - 24px)", transform: "translateX(-12px)" }} />
                )}
                <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-2 transition-all ${isComplete ? "bg-emerald-500 text-white" : isCurrent ? "bg-[#2563EB] text-white ring-4 ring-blue-100" : "bg-slate-100 text-slate-600"}`}>
                  {isComplete ? <CheckCircle2 className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                </div>
                <span className={`text-xs text-center ${isCurrent ? "text-[#2563EB] font-medium" : "text-slate-600"}`}>{milestone.label}</span>
              </div>
            );
          })}
        </div>

        {/* Status Message */}
        <div className="bg-slate-50 rounded-xl p-4 mb-4">
          <p className="text-sm text-slate-600">{getStatusMessage()}</p>
        </div>

        {/* Claim Details */}
        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center gap-2">
            <Car className="w-4 h-4 text-slate-600" />
            <div>
              <p className="text-xs text-slate-600">Vehicle</p>
              <p className="text-sm font-medium text-slate-900 truncate">{claim.vehicle_year_make_model}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-slate-600" />
            <div>
              <p className="text-xs text-slate-600">Submitted</p>
              <p className="text-sm font-medium text-slate-900">{formatDate(claim.created_at)}</p>
            </div>
          </div>
        </div>

        {/* Payout Info */}
        {(claim.status === "approved" || claim.status === "closed") && claim.payout_amount && (
          <div className="mt-4 p-4 bg-emerald-50 rounded-xl border border-emerald-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-emerald-600 uppercase tracking-wide">Approved Payout</p>
                <p className="text-2xl font-bold text-emerald-700 font-['Plus_Jakarta_Sans']">{formatCurrency(claim.payout_amount)}</p>
              </div>
              <CheckCircle2 className="w-10 h-10 text-emerald-500" />
            </div>
          </div>
        )}

        {/* Estimated DV */}
        {!["approved", "closed"].includes(claim.status) && claim.estimated_dv_loss > 0 && (
          <div className="mt-4 p-4 bg-blue-50 rounded-xl border border-blue-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-blue-600 uppercase tracking-wide">Estimated DV Loss</p>
                <p className="text-xl font-bold text-[#2563EB] font-['Plus_Jakarta_Sans']">{formatCurrency(claim.estimated_dv_loss)}</p>
              </div>
              <DollarSign className="w-8 h-8 text-blue-300" />
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-6 py-3 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
        <span className="text-xs text-slate-600">Last updated {formatDate(claim.updated_at || claim.created_at)}</span>
        <div className="flex items-center gap-1 text-[#2563EB] text-sm font-medium group-hover:gap-2 transition-all">
          View Details
          <ChevronRight className="w-4 h-4" />
        </div>
      </div>
    </div>
  );
}

export default function ClaimsStatus() {
  const navigate = useNavigate();
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("search"); // "login", "policy", "search"
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [policyForm, setPolicyForm] = useState({ policyNumber: "", lastName: "" });

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) {
      toast.error("Please enter a claim number, email, or VIN");
      return;
    }
    setLoading(true);
    setSearched(true);
    try {
      const res = await axios.get(`${API}/claims?search=${encodeURIComponent(searchQuery)}`);
      setClaims(res.data);
      if (res.data.length === 0) toast.info("No claims found. Please check your search criteria.");
    } catch (error) {
      console.error("Error searching claims:", error);
      toast.error("Failed to search claims. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginForm.email || !loginForm.password) {
      toast.error("Please enter your email and password");
      return;
    }
    setLoading(true);
    setSearched(true);
    try {
      // Search for claims associated with this email
      const res = await axios.get(`${API}/claims?search=${encodeURIComponent(loginForm.email)}`);
      setClaims(res.data);
      if (res.data.length === 0) {
        toast.info("No claims found for this account. You may need to file a new claim.");
      } else {
        toast.success(`Found ${res.data.length} claim(s) for your account.`);
      }
      setActiveTab("search"); // Switch to search tab to show results
    } catch (error) {
      console.error("Error searching claims:", error);
      toast.error("Unable to retrieve claims. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handlePolicyLookup = async (e) => {
    e.preventDefault();
    if (!policyForm.policyNumber || !policyForm.lastName) {
      toast.error("Please enter your policy number and last name");
      return;
    }
    setLoading(true);
    setSearched(true);
    try {
      // Search by policy number
      const res = await axios.get(`${API}/claims?search=${encodeURIComponent(policyForm.policyNumber)}`);
      setClaims(res.data);
      if (res.data.length === 0) toast.info("No claims found for this policy. You may need to file a new claim.");
    } catch (error) {
      console.error("Error searching by policy:", error);
      toast.error("Failed to lookup policy. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="claims-status-page">
      {/* Hero Section */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 px-6 md:px-12 pt-12 pb-14 mb-8">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-blue-50 px-4 py-2 rounded-full mb-6">
            <div className="w-2 h-2 bg-[#2563EB] rounded-full"></div>
            <span className="text-[#2563EB] text-sm font-medium">Diminished Value Insurance</span>
          </div>
          
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 font-['Plus_Jakarta_Sans'] mb-4">
            Check Your <span className="text-[#2563EB]">Claim Status</span>
          </h1>
          <p className="text-lg text-slate-600 mb-8 max-w-xl mx-auto">
            Track the progress of your diminished value claim in real-time.
          </p>

          {/* Tab Navigation */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex bg-slate-100 rounded-xl p-1">
              <button
                onClick={() => setActiveTab("login")}
                className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-all ${activeTab === "login" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
                data-testid="tab-login"
              >
                <span className="flex items-center gap-2"><User className="w-4 h-4" />Login</span>
              </button>
              <button
                onClick={() => setActiveTab("policy")}
                className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-all ${activeTab === "policy" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
                data-testid="tab-policy"
              >
                <span className="flex items-center gap-2"><FileText className="w-4 h-4" />Policy Number</span>
              </button>
              <button
                onClick={() => setActiveTab("search")}
                className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-all ${activeTab === "search" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
                data-testid="tab-search"
              >
                <span className="flex items-center gap-2"><Search className="w-4 h-4" />Claim / VIN</span>
              </button>
            </div>
          </div>

          {/* Login Form */}
          {activeTab === "login" && (
            <form onSubmit={handleLogin} className="max-w-md mx-auto space-y-4">
              <div className="text-left">
                <Label htmlFor="login-email" className="text-slate-700">Email Address</Label>
                <div className="relative mt-2">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-600" />
                  <Input
                    id="login-email"
                    type="email"
                    placeholder="Enter your email"
                    value={loginForm.email}
                    onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                    className="pl-12 h-12 text-base bg-white border-slate-200 rounded-xl"
                    data-testid="login-email-input"
                  />
                </div>
              </div>
              <div className="text-left">
                <Label htmlFor="login-password" className="text-slate-700">Password</Label>
                <div className="relative mt-2">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-600" />
                  <Input
                    id="login-password"
                    type="password"
                    placeholder="Enter your password"
                    value={loginForm.password}
                    onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                    className="pl-12 h-12 text-base bg-white border-slate-200 rounded-xl"
                    data-testid="login-password-input"
                  />
                </div>
              </div>
              <Button
                type="submit"
                className="w-full h-12 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-medium rounded-xl"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? <span className="flex items-center gap-2"><Clock className="w-4 h-4 animate-spin" />Signing In...</span> : <span className="flex items-center justify-center gap-2">Sign In<ArrowRight className="w-4 h-4" /></span>}
              </Button>
              <p className="text-sm text-slate-600">
                Don't have an account? <Link to="/file-claim" className="text-[#2563EB] hover:underline font-medium">File a claim</Link> to get started.
              </p>
            </form>
          )}

          {/* Policy Number Form */}
          {activeTab === "policy" && (
            <form onSubmit={handlePolicyLookup} className="max-w-md mx-auto space-y-4">
              <div className="text-left">
                <Label htmlFor="policy-number" className="text-slate-700">Policy Number</Label>
                <div className="relative mt-2">
                  <FileText className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-600" />
                  <Input
                    id="policy-number"
                    type="text"
                    placeholder="Enter your policy number (e.g., POL-123456)"
                    value={policyForm.policyNumber}
                    onChange={(e) => setPolicyForm({ ...policyForm, policyNumber: e.target.value })}
                    className="pl-12 h-12 text-base bg-white border-slate-200 rounded-xl"
                    data-testid="policy-number-input"
                  />
                </div>
              </div>
              <div className="text-left">
                <Label htmlFor="policy-lastname" className="text-slate-700">Last Name</Label>
                <div className="relative mt-2">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-600" />
                  <Input
                    id="policy-lastname"
                    type="text"
                    placeholder="Enter your last name"
                    value={policyForm.lastName}
                    onChange={(e) => setPolicyForm({ ...policyForm, lastName: e.target.value })}
                    className="pl-12 h-12 text-base bg-white border-slate-200 rounded-xl"
                    data-testid="policy-lastname-input"
                  />
                </div>
              </div>
              <Button
                type="submit"
                className="w-full h-12 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-medium rounded-xl"
                disabled={loading}
                data-testid="policy-lookup-btn"
              >
                {loading ? <span className="flex items-center gap-2"><Clock className="w-4 h-4 animate-spin" />Looking Up...</span> : <span className="flex items-center justify-center gap-2">Look Up Policy<ArrowRight className="w-4 h-4" /></span>}
              </Button>
              <p className="text-sm text-slate-600">
                Find your policy number on your insurance documents or confirmation email.
              </p>
            </form>
          )}

          {/* Claim/VIN Search Form */}
          {activeTab === "search" && (
            <form onSubmit={handleSearch} className="max-w-xl mx-auto">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-600" />
                  <Input
                    type="text"
                    placeholder="Enter claim number (e.g., CLM-100000)"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-12 h-14 text-base bg-white border-slate-200 rounded-xl shadow-sm"
                    data-testid="claim-status-search"
                  />
                </div>
                <Button
                  type="submit"
                  className="h-14 px-8 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-medium rounded-xl shadow-sm"
                  disabled={loading}
                  data-testid="claim-status-search-btn"
                >
                  {loading ? <span className="flex items-center gap-2"><Clock className="w-4 h-4 animate-spin" />Searching...</span> : <span className="flex items-center gap-2">Track Claim<ArrowRight className="w-4 h-4" /></span>}
                </Button>
              </div>
              <p className="text-slate-600 text-sm mt-3">You can also search by email address or vehicle VIN</p>
            </form>
          )}

          {/* File a Claim CTA */}
          <div className="mt-8 pt-8 border-t border-slate-100">
            <p className="text-slate-600 text-sm mb-3">Don't have a claim yet?</p>
            <Link to="/file-claim">
              <Button variant="outline" className="border-slate-200 text-slate-700 hover:bg-slate-50 gap-2 rounded-xl" data-testid="file-new-claim-btn">
                <Plus className="w-4 h-4" />
                Report an Accident & File a Claim
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Results Section */}
      {searched && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-slate-900 font-['Plus_Jakarta_Sans']">
              {loading ? "Searching..." : `${claims.length} ${claims.length === 1 ? "Claim" : "Claims"} Found`}
            </h2>
            {claims.length > 0 && (
              <Button variant="outline" size="sm" className="border-slate-200" onClick={() => { setSearched(false); setSearchQuery(""); setClaims([]); }}>
                New Search
              </Button>
            )}
          </div>

          {loading ? (
            <div className="grid gap-6 md:grid-cols-2">
              {[1, 2].map((i) => (
                <div key={i} className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                  <Skeleton className="h-20 rounded-none" />
                  <div className="p-6 space-y-4">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-20 w-full" />
                    <Skeleton className="h-4 w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : claims.length > 0 ? (
            <div className="grid gap-6 md:grid-cols-2">
              {claims.map((claim) => <ClaimStatusCard key={claim.id} claim={claim} onClick={() => navigate(`/claims/${claim.claim_id}`)} />)}
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="w-8 h-8 text-slate-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">No Claims Found</h3>
              <p className="text-slate-600 mb-6 max-w-md mx-auto">We couldn't locate a claim with the provided details. Verify your claim number, email, or VIN and try again.</p>
              <div className="flex gap-3 justify-center">
                <Button variant="outline" className="border-slate-200" onClick={() => { setSearched(false); setSearchQuery(""); }}>Search Again</Button>
                <Button className="bg-[#2563EB] hover:bg-[#1D4ED8]" onClick={() => window.location.href = "/file-claim"}>File a New Claim</Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Help Section */}
      {!searched && (
        <div className="mt-8">
          <h2 className="text-xl font-semibold text-slate-900 font-['Plus_Jakarta_Sans'] mb-6 text-center">Need Help?</h2>
          <div className="grid gap-6 md:grid-cols-3">
            <div className="bg-white rounded-xl border border-slate-200 p-6 text-center hover:shadow-md hover:border-slate-300 transition-all">
              <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Phone className="w-6 h-6 text-[#2563EB]" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Call Us</h3>
              <p className="text-slate-600 text-sm mb-3">Mon-Fri, 9am-5pm EST</p>
              <a href="tel:833-567-7729" className="text-[#2563EB] font-medium hover:underline">833-LOSSPAY</a>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-6 text-center hover:shadow-md hover:border-slate-300 transition-all">
              <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-6 h-6 text-[#2563EB]" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Email Support</h3>
              <p className="text-slate-600 text-sm mb-3">We'll respond within 24 hours</p>
              <a href="mailto:claims@losspay.com" className="text-[#2563EB] font-medium hover:underline">claims@losspay.com</a>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-6 text-center hover:shadow-md hover:border-slate-300 transition-all">
              <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <HelpCircle className="w-6 h-6 text-[#2563EB]" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">FAQ</h3>
              <p className="text-slate-600 text-sm mb-3">Find answers to common questions</p>
              <a href="https://youarenotfullycovered.com/#faq" target="_blank" rel="noopener noreferrer" className="text-[#2563EB] font-medium hover:underline">View FAQ</a>
            </div>
          </div>

          {/* Process Explanation */}
          <div className="mt-12 bg-white rounded-2xl border border-slate-200 p-8">
            <h2 className="text-xl font-semibold text-slate-900 font-['Plus_Jakarta_Sans'] mb-6 text-center">How the Claims Process Works</h2>
            <div className="grid gap-6 md:grid-cols-4">
              {milestones.map((milestone, idx) => {
                const Icon = milestone.icon;
                return (
                  <div key={milestone.id} className="relative">
                    {idx < milestones.length - 1 && <div className="hidden md:block absolute top-6 left-full w-full h-0.5 bg-slate-200 -translate-x-1/2" />}
                    <div className="flex flex-col items-center text-center">
                      <div className="w-12 h-12 rounded-full bg-[#2563EB] text-white flex items-center justify-center mb-3">
                        <Icon className="w-6 h-6" />
                      </div>
                      <h4 className="font-semibold text-slate-900 mb-1">{milestone.label}</h4>
                      <p className="text-sm text-slate-600">{milestone.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
